import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CheckIcon,
  TruckIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  UsersIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  Bars3Icon,
  XMarkIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import apiService from '../../services/api';

/* Real screenshots captured live from this system's own demo account —
   not recreations. See public/screens/*.png. */

// Straight-on (no tilt) desktop-monitor device mockup built from a real
// photographed monitor frame (public/mockups/monitor-frame.png) with its
// screen area transparent, so the actual product screenshot sits inside it.
// Pass either a single `src`, or a `screens` list + `activeIndex` to have the
// screen contents slide between screenshots without the frame itself moving.
const MonitorMockup: React.FC<{
  title: string;
  src?: string;
  alt?: string;
  screens?: { src: string; alt: string }[];
  activeIndex?: number;
  className?: string;
}> = ({ title, src, alt, screens, activeIndex = 0, className = '' }) => (
  <div className={`monitor-mockup-wrap mx-auto max-w-md ${className}`}>
    <div className="monitor-mockup">
      <img src="/mockups/monitor-frame.png" alt="" aria-hidden="true" className="monitor-mockup__frame" />
      <div className="monitor-mockup__screen">
        {screens ? (
          <div
            className="monitor-mockup__track"
            style={{
              width: `${screens.length * 100}%`,
              transform: `translateX(-${activeIndex * (100 / screens.length)}%)`,
            }}
          >
            {screens.map((s, i) => (
              <img
                key={i}
                src={s.src}
                alt={s.alt}
                className="monitor-mockup__track-img"
                style={{ width: `${100 / screens.length}%` }}
              />
            ))}
          </div>
        ) : (
          <img src={src} alt={alt} className="monitor-mockup__screen-img" />
        )}
      </div>
    </div>
    <p className="mt-4 text-center text-xs font-semibold uppercase tracking-wide text-gray-400">{title}</p>
  </div>
);

// Counts up from 0 to the stat's numeric value the moment it scrolls into view.
const AnimatedStat: React.FC<{ value: string; className?: string }> = ({ value, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [display, setDisplay] = useState(value.replace(/[\d,.]+/, '0'));

  useEffect(() => {
    const node = ref.current;
    const match = value.match(/^([\d,]+(?:\.\d+)?)(.*)$/);
    if (!node || !match) return;

    const target = parseFloat(match[1].replace(/,/g, ''));
    const suffix = match[2];
    const decimals = match[1].includes('.') ? match[1].split('.')[1].length : 0;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        const duration = 1400;
        const start = performance.now();
        const tick = (now: number) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          const current = target * eased;
          setDisplay(
            (decimals ? current.toFixed(decimals) : Math.round(current).toLocaleString('en-US')) + suffix
          );
          if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.4 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [value]);

  return (
    <div ref={ref} className={className}>
      {display}
    </div>
  );
};

// Reveals a platform-tour row as it scrolls into view: the golden glow behind
// the mockup blooms open first, the screenshot settles in right after, and
// the copy/bullets on the text side fade and slide in from their entry edge.
const FeatureRow: React.FC<{
  id?: string;
  reverse?: boolean;
  eyebrow: string;
  title: string;
  description: string;
  bullets: string[];
  screenshot: { title: string; src: string; alt: string };
}> = ({ id, reverse, eyebrow, title, description, bullets, screenshot }) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = rowRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      id={id}
      ref={rowRef}
      className={`feature-row scroll-mt-28 grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16 ${visible ? 'feature-row--visible' : ''}`}
    >
      <div
        className={`feature-row__text ${reverse ? 'lg:order-2 feature-row__text--from-right' : 'feature-row__text--from-left'}`}
      >
        <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-copper-700">
          {eyebrow}
          <span className="feature-row__eyebrow-bar" />
        </span>
        <h3 className="mt-3 text-2xl md:text-3xl font-bold text-navy-900">{title}</h3>
        <p className="mt-4 text-lg leading-relaxed text-navy-500/80">{description}</p>
        <ul className="mt-6 space-y-3">
          {bullets.map((bullet, i) => (
            <li
              key={i}
              className="feature-row__bullet flex items-start gap-3"
              style={{ transitionDelay: visible ? `${280 + i * 90}ms` : '0ms' }}
            >
              <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-copper-100 text-copper-700">
                <CheckIcon className="h-3.5 w-3.5" />
              </span>
              <span className="text-navy-700">{bullet}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className={reverse ? 'lg:order-1' : ''}>
        <div className="relative mx-auto max-w-md">
          <div className="feature-row__glow absolute -inset-8 rounded-[3rem] bg-copper-400/15 blur-2xl"></div>
          <div className="feature-row__visual relative">
            <MonitorMockup
              title={screenshot.title}
              src={screenshot.src}
              alt={screenshot.alt}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Types the text out once it scrolls into view. Screen readers get the full
// text from a visually hidden copy instead of hearing it letter by letter.
const TypingText: React.FC<{
  text: string;
  speed?: number;
  delay?: number;
  className?: string;
}> = ({ text, speed = 45, delay = 0, className = '' }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [displayText, setDisplayText] = useState('');
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    let index = 0;
    let timer: ReturnType<typeof setInterval>;
    const delayTimer = setTimeout(() => {
      timer = setInterval(() => {
        index++;
        setDisplayText(text.slice(0, index));
        if (index >= text.length) clearInterval(timer);
      }, speed);
    }, delay);
    return () => {
      clearTimeout(delayTimer);
      clearInterval(timer);
    };
  }, [started, text, speed, delay]);

  return (
    <span ref={ref} className={`block ${className}`}>
      <span className="sr-only">{text}</span>
      <span aria-hidden="true">{displayText || ' '}</span>
    </span>
  );
};

const Landing: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'month' | 'year'>('month');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // The nav sits transparent on the hero video until the page scrolls (or the
  // mobile menu opens), then turns white with the dark logo.
  const solidNav = scrolled || mobileMenuOpen;
  const navLink = `text-sm font-medium transition-colors hover:text-copper-500 ${solidNav ? 'text-gray-700' : 'text-white'}`;

  const { data: subscriptionPlans } = useQuery({
    queryKey: ['publicSubscriptionPlans'],
    queryFn: () => apiService.getSubscriptionPlans(),
  });

  const features = [
    {
      icon: TruckIcon,
      title: 'إدارة الرحلات',
      description: 'إدارة شاملة للرحلات مع تتبع الحافلات والمسارات'
    },
    {
      icon: CalendarIcon,
      title: 'نظام الحجوزات',
      description: 'حجز سريع وسهل مع إدارة العملاء والمرافقين'
    },
    {
      icon: BuildingOfficeIcon,
      title: 'إدارة السكن',
      description: 'إدارة الفنادق والشقق مع تتبع الغرف المتاحة'
    },
    {
      icon: ChartBarIcon,
      title: 'التقارير المالية',
      description: 'تقارير شاملة للأرباح والخسائر والمبيعات'
    },
    {
      icon: UsersIcon,
      title: 'إدارة المستخدمين',
      description: 'إدارة صلاحيات المستخدمين والموظفين'
    },
    {
      icon: ShieldCheckIcon,
      title: 'الأمان والحماية',
      description: 'نظام أمان متقدم مع تشفير البيانات'
    }
  ];

  const stats = [
    { number: '50+', label: 'مكتب نشط' },
    { number: '300+', label: 'رحلة مُدارة' },
    { number: '5,000+', label: 'حجز مكتمل' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 rtl">
      {/* Top Navigation — transparent over the hero video, white once scrolled */}
      <div className="fixed inset-x-0 top-0 z-50">
        <nav className={`w-full transition-colors duration-300 ${solidNav ? 'bg-white shadow-md' : 'bg-transparent'}`}>
          <div className="relative mx-auto flex max-w-7xl items-center justify-between px-4 py-2 sm:px-6 lg:px-8">
            <img
              src={solidNav ? '/logo-transparent4.png' : '/logo-transparent3.png'}
              alt="ميسر"
              className="h-24 w-auto -my-5 scale-75 sm:h-32 sm:-my-9"
            />

            {/* Desktop Navigation — centered in the bar */}
            <div className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 items-center gap-7 md:flex">
              <a href="#features" className={navLink}>
                من نحن
              </a>
              <a href="#why-moyasar" className={navLink}>
                لماذا ميسر
              </a>
              <a href="#pricing" className={navLink}>
                الأسعار
              </a>
              <a href="#contact" className={navLink}>
                اتصل بنا
              </a>
            </div>

            {/* Actions — stay pinned to their edge */}
            <div className="hidden items-center gap-4 md:flex">
              <Link to="/vendor-login" className={navLink}>
                تسجيل الدخول
              </Link>
              <Link
                to="/vendor-register"
                className="rounded-xl bg-navy-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-copper-500 hover:text-navy-900"
              >
                التسجيل
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="القائمة"
                className={`transition-colors hover:text-copper-600 ${solidNav ? 'text-gray-700' : 'text-white'}`}
              >
                {mobileMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="border-t border-gray-100 md:hidden">
              <div className="space-y-1 px-3 pt-2 pb-3">
                <a href="#features" className="block px-3 py-2 text-gray-700 hover:text-copper-600">
                  من نحن
                </a>
                <a href="#why-moyasar" className="block px-3 py-2 text-gray-700 hover:text-copper-600">
                  لماذا ميسر
                </a>
                <a href="#pricing" className="block px-3 py-2 text-gray-700 hover:text-copper-600">
                  الأسعار
                </a>
                <a href="#contact" className="block px-3 py-2 text-gray-700 hover:text-copper-600">
                  اتصل بنا
                </a>
                <Link
                  to="/vendor-login"
                  className="block px-3 py-2 text-gray-700 hover:text-copper-600"
                >
                  تسجيل الدخول
                </Link>
                <Link
                  to="/vendor-register"
                  className="mx-3 block rounded-xl bg-navy-900 px-3 py-2 text-center font-semibold text-white hover:bg-copper-500 hover:text-navy-900"
                >
                  التسجيل
                </Link>
              </div>
            </div>
          )}
        </nav>
      </div>

      {/* Hero Section */}
      <section className="relative flex min-h-screen items-center overflow-hidden">
        {/* Background video, darkened so the copy stays readable */}
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="/hp-poster.jpg"
          className="absolute inset-0 h-full w-full object-cover"
        >
          <source src="/hp.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/40"></div>

        {/* Slow drifting background blobs for a bit of life behind the content */}
        <div className="hero-blob-a pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-copper-400/15 blur-3xl"></div>
        <div className="hero-blob-b pointer-events-none absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-navy-400/10 blur-3xl"></div>

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="hero-text max-w-xl mx-auto lg:mx-0 text-center lg:text-right">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight text-white">
              <TypingText text="منصّة واحدة لإدارة" speed={60} />
              <TypingText text="جميع رحلاتك" speed={70} delay={300} className="text-copper-400" />
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/85 max-w-xl mx-auto lg:mx-0">
              <TypingText
                text="من أول حجز إلى آخر تقرير مالي، يجمع ميسر رحلاتك وحافلاتك وسكن عملائك في منصة واحدة سهلة وسريعة."
                speed={30}
                delay={500}
              />
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link
                to="/vendor-register"
                className="bg-navy-900 text-white px-8 py-3 rounded-xl font-bold hover:bg-copper-600 transition-colors flex items-center justify-center shadow-lg shadow-copper-500/20"
              >
                ابدأ الآن مجاناً
                <ArrowRightIcon className="h-5 w-5 mr-2" />
              </Link>
              <Link
                to="/vendor-login"
                className="border-2 border-white/40 bg-white/10 text-white px-8 py-3 rounded-xl font-semibold hover:bg-copper-500 hover:text-navy-900 hover:border-copper-500 transition-colors"
              >
                تسجيل الدخول
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats — floating cards below the hero */}
      <section className="relative bg-parchment-50 pt-16 pb-20">
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="rounded-3xl bg-white px-6 py-8 text-center shadow-xl ring-1 ring-black/5"
              >
                <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-copper-500"></div>
                <AnimatedStat value={stat.number} className="text-3xl md:text-4xl font-bold text-navy-900 mb-1" />
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform tour — alternating feature rows using real screens */}
      <section id="features" className="py-4 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              تعرّف على ميسر عن قرب
            </h2>
            <div className="mx-auto mb-4 h-1 w-16 rounded-full bg-copper-500"></div>
            <p className="text-xl text-navy-500/80 max-w-2xl mx-auto">
              جولة سريعة داخل الشاشات التي يستخدمها فريقك كل يوم
            </p>
          </div>

          <div className="space-y-24 lg:space-y-32">
            <FeatureRow
              id="service-reports"
              eyebrow="التقارير المالية"
              title="لوحة أرقام تتحدث لغتك"
              description="كل ريال يدخل ويخرج من مكتبك، موثّق ومصنّف تلقائيًا في تقرير واحد واضح."
              bullets={[
                'نظرة فورية على المتبقي، المدفوع، وإجمالي الإيرادات',
                'تقويم مزدوج هجري وميلادي داخل كل تقرير',
                'تبويبات جاهزة: الحجوزات، المالية، الرحلات، والعملاء'
              ]}
              screenshot={{ title: 'التقارير', src: '/screens/reports.png', alt: 'شاشة التقارير المالية الحقيقية من نظام ميسر' }}
            />

            <FeatureRow
              id="service-bookings"
              reverse
              eyebrow="نظام الحجوزات"
              title="حجز ذكي، مقعدًا بمقعد"
              description="مصفوفة مقاعد الحافلة الحقيقية أمامك، فتحجز بدقة بدل التخمين."
              bullets={[
                'خريطة مقاعد تفاعلية لكل حافلة توضح المتاح من أول نظرة',
                'ربط كل مقعد باسم المسافر ورقم جواله مباشرة',
                'تمييز فوري لمقاعد الباب الأمامي والخلفي ودورة المياه'
              ]}
              screenshot={{ title: 'اختيار المقاعد — حافلة H-49', src: '/screens/seats.png', alt: 'شاشة اختيار المقاعد الحقيقية من نظام ميسر' }}
            />

            <FeatureRow
              id="service-accommodations"
              eyebrow="إدارة السكن"
              title="سكن منظّم لكل رحلة"
              description="كل فندق وشقة تتعامل معهم، بتقييمهم وسعتهم وسعرهم، في قائمة واحدة قابلة للفلترة."
              bullets={[
                'تسجيل كل منشأة سكن بتقييمها وسعرها لليلة الواحدة',
                'فلترة فورية حسب المدينة — مكة المكرمة والمدينة المنورة',
                'متابعة حيّة لعدد الغرف والسعة المتاحة في كل منشأة'
              ]}
              screenshot={{ title: 'إدارة السكن', src: '/screens/accommodations.png', alt: 'شاشة إدارة السكن الحقيقية من نظام ميسر' }}
            />
          </div>
        </div>
      </section>

      {/* Why Moyasar — inset rounded card instead of a full-bleed band */}
      <section id="why-moyasar" className="relative scroll-mt-28 bg-white py-14 sm:py-20">
        <div className="relative mx-4 overflow-hidden rounded-[2.5rem] bg-parchment-100 px-4 py-14 sm:mx-6 sm:px-8 sm:py-16 lg:mx-auto lg:max-w-6xl lg:px-12">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.4]"
            style={{
              backgroundImage:
                'radial-gradient(circle at 15% 20%, rgba(178,120,60,0.12) 0, transparent 35%), radial-gradient(circle at 85% 75%, rgba(10,12,28,0.06) 0, transparent 40%)',
            }}
          ></div>
          <div className="relative">
            <div className="text-center mb-10">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-copper-700">لماذا ميسر؟</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold text-navy-900 mb-4">
                لأن اسمنا معناه السهولة
              </h2>
              <div className="mx-auto mb-4 h-1 w-16 rounded-full bg-copper-500"></div>
              <p className="text-xl text-navy-500/80 max-w-2xl mx-auto">
                من الحجز الأول إلى آخر تقرير، صممنا كل زاوية في ميسر لتكون أوضح وأسرع وأقرب لطريقة عملك
              </p>
            </div>

            <div className="grid grid-cols-1 gap-x-8 gap-y-2 md:grid-cols-2 lg:grid-cols-3">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="group flex items-start gap-4 border-b border-gray-200/80 py-6"
                >
                  <span className="w-10 shrink-0 text-3xl font-black leading-none text-copper-200 transition-colors group-hover:text-copper-400">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-lg bg-navy-900 text-copper-300 transition-colors group-hover:bg-copper-500 group-hover:text-navy-900">
                      <feature.icon className="h-4 w-4" />
                    </div>
                    <h3 className="text-base font-bold text-navy-900">{feature.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-navy-500/80">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              خطط الاشتراك
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              اختر الخطة المناسبة لاحتياجاتك وابدأ في تطوير أعمالك
            </p>

            <div className="mt-8 inline-flex rounded-xl bg-gray-100 p-1">
              <button
                onClick={() => setBillingCycle('month')}
                className={`px-6 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  billingCycle === 'month' ? 'bg-white text-navy-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                شهري
              </button>
              <button
                onClick={() => setBillingCycle('year')}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  billingCycle === 'year' ? 'bg-white text-navy-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                سنوي
                <span className="rounded-full bg-copper-100 px-2 py-0.5 text-[10px] font-bold text-copper-700">وفّر أكثر</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-start">
            {subscriptionPlans
              ?.filter((plan: any) => (billingCycle === 'year' ? plan.duration === 12 : plan.duration !== 12))
              .map((plan: any) => {
                const isFeatured = plan.name.includes('مميز');
                const checkBadge = isFeatured
                  ? 'bg-copper-500/20 text-copper-300'
                  : 'bg-copper-100 text-copper-700';
                return (
              <div key={plan._id} className={`relative flex h-full flex-col rounded-2xl p-6 ${
                isFeatured
                  ? 'bg-navy-900 text-white shadow-2xl shadow-navy-900/30 ring-1 ring-copper-500/30 lg:scale-105'
                  : 'bg-white text-navy-900 shadow-sm ring-1 ring-gray-100'
              }`}>
                {isFeatured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 transform">
                    <span className="rounded-full bg-copper-500 px-4 py-1 text-sm font-bold text-navy-900">
                      الأكثر شعبية
                    </span>
                  </div>
                )}

                <div className="text-center mb-5">
                  <h3 className={`text-xl font-bold mb-1.5 ${isFeatured ? 'text-white' : 'text-navy-900'}`}>
                    {plan.name}
                  </h3>
                  <div className={`text-3xl font-bold mb-1.5 ${isFeatured ? 'text-white' : 'text-navy-800'}`}>
                    {plan.price} ريال
                    <span className={`text-base font-normal ${isFeatured ? 'text-white/60' : 'text-gray-500'}`}>
                      /{billingCycle === 'year' ? 'سنة' : 'شهر'}
                    </span>
                  </div>
                  <p className={`text-sm ${isFeatured ? 'text-white/70' : 'text-gray-600'}`}>{plan.description}</p>
                </div>

                <div className="space-y-2.5 mb-5 flex-grow text-sm">
                  <div className="flex items-center gap-2.5">
                    <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${checkBadge}`}>
                      <CheckIcon className="h-3 w-3" />
                    </span>
                    <span className={isFeatured ? 'text-white/85' : 'text-gray-700'}>حتى {plan.maxUsers} مستخدم</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${checkBadge}`}>
                      <CheckIcon className="h-3 w-3" />
                    </span>
                    <span className={isFeatured ? 'text-white/85' : 'text-gray-700'}>حتى {plan.maxBuses} حافلة</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${checkBadge}`}>
                      <CheckIcon className="h-3 w-3" />
                    </span>
                    <span className={isFeatured ? 'text-white/85' : 'text-gray-700'}>حتى {plan.maxTrips} رحلة</span>
                  </div>
                  {plan.features?.map((feature: string, index: number) => (
                    <div key={index} className="flex items-center gap-2.5">
                      <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${checkBadge}`}>
                        <CheckIcon className="h-3 w-3" />
                      </span>
                      <span className={isFeatured ? 'text-white/85' : 'text-gray-700'}>{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-auto">
                  <Link
                    to="/vendor-register"
                    className={`w-full text-center py-2.5 px-4 rounded-xl font-semibold transition-colors block ${
                      isFeatured
                        ? 'bg-copper-500 text-navy-900 hover:bg-copper-400'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    اختر هذه الخطة
                  </Link>
                </div>
              </div>
                );
              })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              ماذا يقول عملاؤنا
            </h2>
            <p className="text-xl text-gray-600">
              انضم إلى آلاف البائعين الراضين عن خدماتنا
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                quote: 'نظام ممتاز ساعدني في إدارة أعمالي بكفاءة عالية. التقارير المالية دقيقة والواجهة سهلة الاستخدام.',
                name: 'أحمد محمد',
                role: 'مكتب الرحلات العربية',
              },
              {
                quote: 'خدمة العملاء ممتازة والدعم الفني متاح على مدار الساعة. النظام ساعدني في زيادة أرباحي بنسبة 40%.',
                name: 'سارة أحمد',
                role: 'مؤسسة الحج والعمرة',
              },
              {
                quote: 'أفضل نظام لإدارة الحج والعمرة. سهل الاستخدام ويوفر جميع المميزات التي نحتاجها.',
                name: 'خالد العتيبي',
                role: 'شركة الرحلات المتحدة',
              },
            ].map((t, i) => (
              <div key={i} className="relative rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-100">
                <div className="absolute inset-x-0 top-0 h-1 rounded-t-2xl bg-gradient-to-r from-transparent via-copper-400 to-transparent"></div>
                <span className="pointer-events-none absolute top-4 left-6 font-serif text-6xl leading-none text-copper-100">
                  &ldquo;
                </span>
                <div className="relative flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <StarIcon key={i} className="h-4 w-4 text-copper-500" />
                  ))}
                </div>
                <p className="relative text-navy-700 leading-relaxed">"{t.quote}"</p>
                <div className="relative mt-6 border-t border-gray-100 pt-4">
                  <div className="font-bold text-navy-900">{t.name}</div>
                  <div className="text-sm text-copper-600">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section — same card treatment as the "why Moyasar" section */}
      <section className="py-20 bg-parchment-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-parchment-100 px-8 py-12 sm:px-16 sm:py-16 text-center shadow-2xl shadow-navy-900/10">
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.4]"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 15% 20%, rgba(178,120,60,0.12) 0, transparent 35%), radial-gradient(circle at 85% 75%, rgba(10,12,28,0.06) 0, transparent 40%)',
              }}
            ></div>

            <h2 className="relative text-2xl sm:text-3xl md:text-4xl font-bold text-navy-900">
              ابدأ رحلتك مع ميسر اليوم
            </h2>
            <p className="relative mt-4 max-w-xl mx-auto text-base sm:text-lg text-navy-500/80">
              انضم إلى مئات المكاتب التي تدير رحلاتها بثقة وسهولة، من الحج والعمرة إلى الرحلات السياحية وغيرها — بلا أوراق، وبلا تعقيد.
            </p>
            <div className="relative mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/vendor-register"
                className="bg-navy-900 text-white px-7 py-3 rounded-xl font-bold hover:bg-navy-800 transition-colors shadow-lg"
              >
                التسجيل مجاناً
              </Link>
              <Link
                to="/vendor-login"
                className="border-2 border-navy-900/20 bg-white/40 backdrop-blur text-navy-900 px-7 py-3 rounded-xl font-semibold hover:bg-white/70 transition-colors"
              >
                تسجيل الدخول
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="relative bg-gray-900 text-white py-12">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-copper-500 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <img src="/logo-transparent3.png" alt="ميسر" className="mb-4 h-20 w-auto" />
              <p className="text-gray-400">
                منصة متكاملة لإدارة الرحلات والحجوزات والسكن، بأحدث التقنيات وأفضل الخدمات
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">الخدمات</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#why-moyasar" className="transition-colors hover:text-copper-400">إدارة الرحلات</a></li>
                <li><a href="#service-bookings" className="transition-colors hover:text-copper-400">نظام الحجوزات</a></li>
                <li><a href="#service-accommodations" className="transition-colors hover:text-copper-400">إدارة السكن</a></li>
                <li><a href="#service-reports" className="transition-colors hover:text-copper-400">التقارير المالية</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">الدعم</h3>
              <ul className="space-y-2 text-gray-400">
                <li>الدعم الفني</li>
                <li>الأسئلة الشائعة</li>
                <li>اتصل بنا</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">معلومات الاتصال</h3>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-center gap-2">
                  <EnvelopeIcon className="h-4 w-4 flex-shrink-0 text-copper-400" />
                  <span dir="ltr" className="text-right">info@alharamain.com</span>
                </li>
                <li className="flex items-center gap-2">
                  <PhoneIcon className="h-4 w-4 flex-shrink-0 text-copper-400" />
                  <span dir="ltr" className="text-right">+966 50 123 4567</span>
                </li>
                <li className="flex items-center gap-2">
                  <MapPinIcon className="h-4 w-4 flex-shrink-0 text-copper-400" />
                  <span>المملكة العربية السعودية</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} ميسر. جميع الحقوق محفوظة.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
